import type { BlogPost, Product, Service } from "@latam-energy/types";

export type PageMetadataInput = {
  title: string;
  description: string;
  pathname?: string;
  image?: string;
  noIndex?: boolean;
  siteUrl?: string;
};

export const defaultImage = "/og-image.svg";

export function absoluteUrl(pathname = "/", siteUrl = "https://latamenergyservices.com") {
  if (pathname.startsWith("http")) return pathname;
  return `${siteUrl.replace(/\/$/, "")}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
}

export function generatePageMetadata({
  title,
  description,
  pathname = "/",
  image = defaultImage,
  noIndex = false,
  siteUrl = "https://latamenergyservices.com"
}: PageMetadataInput) {
  const canonical = absoluteUrl(pathname, siteUrl);
  const imageUrl = absoluteUrl(image, siteUrl);

  return {
    title,
    description,
    alternates: { canonical },
    openGraph: {
      title,
      description,
      url: canonical,
      siteName: "Latam Energy Services",
      locale: "es_PE",
      type: "website",
      images: [{ url: imageUrl, width: 1200, height: 630, alt: title }]
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [imageUrl]
    },
    robots: noIndex ? { index: false, follow: false } : { index: true, follow: true }
  };
}

export function organizationJsonLd(siteUrl: string, contact: { email: string; telephone: string; address: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Latam Energy Services",
    url: siteUrl,
    email: contact.email,
    telephone: contact.telephone,
    address: {
      "@type": "PostalAddress",
      addressLocality: contact.address,
      addressCountry: "PE"
    },
    areaServed: ["Peru", "Latinoamerica"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: contact.email,
        telephone: contact.telephone,
        availableLanguage: ["es"]
      }
    ],
    knowsAbout: [
      "Automatizacion industrial",
      "Instrumentacion y control",
      "Soluciones electricas",
      "Eficiencia energetica industrial"
    ]
  };
}

export function websiteJsonLd(siteUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Latam Energy Services",
    url: siteUrl,
    inLanguage: "es-PE",
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteUrl.replace(/\/$/, "")}/productos?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
}

export function breadcrumbsJsonLd(items: { name: string; href: string }[], siteUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.href, siteUrl)
    }))
  };
}

export function serviceJsonLd(service: Service, siteUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.summary,
    areaServed: "Latinoamerica",
    provider: { "@type": "Organization", name: "Latam Energy Services", url: siteUrl }
  };
}

export function productJsonLd(product: Product, siteUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.shortDescription,
    brand: { "@type": "Brand", name: product.brand },
    model: product.model,
    category: product.category,
    image: product.gallery.map((image) => absoluteUrl(image, siteUrl)),
    url: absoluteUrl(`/productos/${product.slug}`, siteUrl)
  };
}

export function articleJsonLd(post: BlogPost, siteUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.summary,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: "es-PE",
    url: absoluteUrl(`/blog/${post.slug}`, siteUrl),
    author: { "@type": "Organization", name: "Latam Energy Services" },
    publisher: { "@type": "Organization", name: "Latam Energy Services" }
  };
}
